import {useMemo} from 'react'

import {APPROVAL_MODES, type SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'

import {APPROVAL_MODE_PRESENTATION, APPROVAL_MODE_SHOW_DESCRIPTION} from '../utils/approval-mode-presentation'

export interface PermissionsCommand {
  key: string
  displayKey?: string
  description: string
  appendSpace?: boolean
  /** Marks the mode the session is already in, so the menu can indicate it. */
  current?: boolean
}

const PERMISSIONS_COMMAND: PermissionsCommand = {
  key: '/permissions',
  description: 'Configure tool permissions',
  appendSpace: true,
}

const ALLOW_ALL_COMMAND: PermissionsCommand = {
  key: '/allow-all',
  description: 'Configure automatic tool approval',
  appendSpace: true,
}

const ROOT_COMMANDS = [PERMISSIONS_COMMAND, ALLOW_ALL_COMMAND]

// `/allow-all`'s boolean spellings, paired with the mode each one selects so the
// alias can be filtered against the same policy as `/permissions`.
const ALLOW_ALL_OPTIONS: Array<{mode: SessionApprovalMode; option: PermissionsCommand}> = [
  {mode: 'allow-all', option: {key: 'on', description: 'Auto-approve all tool calls this session'}},
  {mode: 'manual', option: {key: 'off', description: 'Stop auto-approving tool calls'}},
]

const SHOW_OPTION = {key: 'show', description: APPROVAL_MODE_SHOW_DESCRIPTION}
const ALLOW_ALL_SHOW_OPTION = {key: 'show', description: 'Show whether auto-approve is on'}

export interface PermissionsAutocompleteOptions {
  /**
   * Modes the relay would accept, from `readSelectableApprovalModes`. Defaults to
   * every mode, which suits a draft composer with no session to constrain it.
   */
  selectableModes?: readonly SessionApprovalMode[]
  /** The mode the session is in. Always offered, even if no longer selectable. */
  currentMode?: SessionApprovalMode
}

function optionsFor(prefix: string, options: PermissionsCommand[], typed: string) {
  return options
    .map(option => ({...option, key: `${prefix} ${option.key}`, displayKey: option.key}))
    .filter(option => option.key.startsWith(typed) && option.key !== typed)
}

/**
 * Returns tool-permission commands that match the current input text as a prefix.
 *
 * Two levels: the command names first, then that command's arguments once its
 * name is complete. `/permissions` lists the session's approval modes and
 * `/allow-all` keeps its older on/off spelling, but both are narrowed to the
 * modes the relay would accept so neither offers a choice that gets rejected.
 */
export function usePermissionsAutocomplete(
  currentText: string,
  {selectableModes = APPROVAL_MODES, currentMode}: PermissionsAutocompleteOptions = {},
): PermissionsCommand[] {
  return useMemo(() => {
    const typed = currentText.trimStart()
    if (!typed) return []

    // A locked session offers no mode at all, but `show` still reports the mode
    // it is in, so both commands keep their reporting argument either way.
    const offersMode = (mode: SessionApprovalMode) => selectableModes.includes(mode) || mode === currentMode

    if (typed.startsWith(`${PERMISSIONS_COMMAND.key} `)) {
      const modeOptions = APPROVAL_MODES.filter(offersMode).map(mode => ({
        key: mode,
        description: APPROVAL_MODE_PRESENTATION[mode].description,
        ...(mode === currentMode ? {current: true} : {}),
      }))
      return optionsFor(PERMISSIONS_COMMAND.key, [...modeOptions, SHOW_OPTION], typed)
    }

    if (typed.startsWith(`${ALLOW_ALL_COMMAND.key} `)) {
      const booleanOptions = ALLOW_ALL_OPTIONS.filter(({mode}) => offersMode(mode)).map(({mode, option}) =>
        mode === currentMode ? {...option, current: true} : option,
      )
      return optionsFor(ALLOW_ALL_COMMAND.key, [...booleanOptions, ALLOW_ALL_SHOW_OPTION], typed)
    }

    return ROOT_COMMANDS.filter(command => command.key.startsWith(typed))
  }, [currentMode, currentText, selectableModes])
}
